// OrderExportService.ts
// Centralized service for exporting orders via different channels

import { OrderResponse } from './OrderService';
import { apiService } from './ApiService';
import { composeNumberRequestLabel, requestedOriginalTerm } from '../utils/partLabel';
import { toRealDotAmount } from '../utils/inventoryAvailability';

/**
 * Live cart state to include in the PDF so manual overrides are reflected.
 *
 * discount_override  – fully-resolved discount snapshot as GRAND totals
 *                      (parts + labor, flagged via includes_labor) with the
 *                      extra (custom) discount already folded in.
 * items_override     – items array with the panel's effective unit price
 *                      (manual → selected pricing option → integration price)
 *                      baked into structured_data.part_data.price. When
 *                      provided, the backend uses this array instead of
 *                      re-reading from the DB.
 * tax_rate           – the panel's current (possibly user-edited) tax rate in
 *                      percent; overrides the org VAT rate for this PDF.
 */
export interface PdfPayload {
  discount_override?: Record<string, any> | null;
  items_override?: any[] | null;
  tax_rate?: number | null;
}

class OrderExportService {

  // ── PDF generation helpers ────────────────────────────────────────

  /**
   * Trigger a direct browser download of the quotation PDF generated by the backend.
   * Accepts an optional `payload` with live cart state (manual prices, discount, etc.)
   * so the PDF reflects exactly what the user sees on screen.
   */
  async downloadQuotationPdf(order: OrderResponse, payload?: PdfPayload): Promise<void> {
    const response = await apiService.post<Blob>(
      `/api/user/orders/${order.id}/quotation-pdf`,
      payload || {},
      { responseType: 'blob' }
    );
    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `quotation_${order.external_order_id}.pdf`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  /**
   * Ask the backend to generate the PDF and email it (with PDF attachment) to the recipient.
   */
  async sendQuotationPdfViaEmail(order: OrderResponse, recipientEmail?: string, payload?: PdfPayload): Promise<void> {
    const params = new URLSearchParams({ channel: 'email' });
    if (recipientEmail) params.append('recipient', recipientEmail);
    await apiService.post(`/api/user/orders/${order.id}/share-pdf?${params.toString()}`, payload || {});
  }

  /**
   * Ask the backend to generate the PDF, then:
   *  1. Download the PDF locally so the user has it.
   *  2. Open a WhatsApp share link pre-filled with a summary message.
   */
  async sendQuotationPdfViaWhatsApp(order: OrderResponse, payload?: PdfPayload): Promise<void> {
    const response = await apiService.post<{
      status: string;
      whatsapp_url: string;
      pdf_base64: string;
      filename: string;
    }>(`/api/user/orders/${order.id}/share-pdf?channel=whatsapp`, payload || {});

    const { whatsapp_url, pdf_base64, filename } = response.data;

    // 1. Auto-download the PDF
    const byteCharacters = atob(pdf_base64);
    const byteArray = new Uint8Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteArray[i] = byteCharacters.charCodeAt(i);
    }
    const blob = new Blob([byteArray], { type: 'application/pdf' });
    const dlUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = dlUrl;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(dlUrl);

    // 2. Open WhatsApp share
    window.open(whatsapp_url, '_blank');
  }

  /**
   * Export order to CSV in the STANDARD export format shared by every
   * parts/cart/order CSV — OE#, Requested, Description, Qty, Unit (CUR),
   * Total (CUR). `currency` (the caller's effective org/user currency) goes
   * into the Unit/Total headers; prices are raw numbers so a spreadsheet can
   * sum them. Unit is the backend-persisted part_data price (no live cart
   * overrides here) and left blank when the part isn't priced.
   */
  exportOrderToCSV(order: OrderResponse, currency?: string, numberFormat?: string): void {
    const parts = order.parts_in_order?.items || [];
    const curSuffix = currency ? ` (${currency})` : '';
    // Same money normalization as the cart CSV: in dot-format orgs a value may
    // be a misparsed dot-thousands decimal (7.817 = 7,817) — toRealDotAmount
    // recovers it while leaving already-real integers untouched.
    const csvNum = (v: number) => numberFormat === 'dot' ? Math.round(toRealDotAmount(v)) : Math.round(v * 100) / 100;
    const headers = ['OE#', 'Requested', 'Description', 'Qty', `Unit${curSuffix}`, `Total${curSuffix}`];
    const rows = parts.map((item: any) => {
      const pd = item.structured_data?.part_data || {};
      const PD = item.structured_data?.PART_DATA || {};
      const oe = PD.PART_ID || pd.part_number || '';
      const description = item.translated_description || PD.DESCRIPTION || pd.description || '';
      // Requested = ONLY the user's own line. The stored query is sometimes the
      // full resolved label with the verbatim term as a parenthetical;
      // requestedOriginalTerm pulls it out. Shared by all three CSV exports.
      const rawQuery = String(item.query || pd.query || PD.QUERY || '').trim();
      const requested = composeNumberRequestLabel(requestedOriginalTerm(rawQuery), description);
      const qty = item.quantity || 1;
      const priceRaw = PD.PRICE ?? pd.price;
      const unit = priceRaw != null && priceRaw !== '' && isFinite(Number(priceRaw)) ? Number(priceRaw) : null;
      return [oe, requested, description, qty, unit != null ? csvNum(unit) : '', unit != null ? csvNum(unit * qty) : ''];
    });

    // RFC-4180 quoting — free-text cells (requested, description) may carry
    // commas, quotes, or newlines.
    const csvCell = (v: any) => {
      const s = String(v ?? '');
      return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    // Blob download (NOT a data: URI — encodeURI leaves '#' unescaped, so the
    // "OE#" header would truncate a data URI at the fragment). A UTF-8 BOM
    // keeps Excel's accented text correct.
    const csv = [headers, ...rows].map((e: any[]) => e.map(csvCell).join(',')).join('\r\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `order_${order.external_order_id}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  /**
   * Send order via email
   */
  sendOrderViaEmail(order: OrderResponse): void {
    const parts = order.parts_in_order?.items || [];
    const vin = order.vin || order.parts_in_order?.vins?.[0] || 'N/A';
    
    // Handle different data structures for part data
    const partsText = parts.map((item: any, index: number) => {
      const partId = item.structured_data?.PART_DATA?.PART_ID || 
                     item.structured_data?.part_data?.part_number || 
                     'N/A';
      const description = item.structured_data?.PART_DATA?.DESCRIPTION || 
                         item.structured_data?.part_data?.description || 
                         'No description';
      return `${index + 1}. ${partId} - ${description} (Qty: ${item.quantity})`;
    }).join('\n');

    const subject = encodeURIComponent(`Order Details - ${order.external_order_id}`);
    const body = encodeURIComponent(`
Order ID: ${order.external_order_id}
Order Name: ${order.name}
Transaction ID: ${order.transaction_id}
Status: ${order.status.toUpperCase()}
Created: ${new Date(order.created_at).toLocaleDateString()}
VIN: ${vin}
Total Parts: ${order.parts_in_order?.total_items || 0}

Parts List:
${partsText}
    `);

    window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
  }

  /**
   * Send order via WhatsApp
   */
  sendOrderViaWhatsApp(order: OrderResponse): void {
    const parts = order.parts_in_order?.items || [];
    const vin = order.vin || order.parts_in_order?.vins?.[0] || 'N/A';
    
    // Handle different data structures for part data
    const partsText = parts.map((item: any, index: number) => {
      const partId = item.structured_data?.PART_DATA?.PART_ID || 
                     item.structured_data?.part_data?.part_number || 
                     'N/A';
      const description = item.structured_data?.PART_DATA?.DESCRIPTION || 
                         item.structured_data?.part_data?.description || 
                         'No description';
      return `${index + 1}. ${partId} - ${description} (Qty: ${item.quantity})`;
    }).join('\n');

    const message = encodeURIComponent(`
🛒 *Order Details*

📋 *Order ID:* ${order.external_order_id}
📛 *Order Name:* ${order.name}
💳 *Transaction ID:* ${order.transaction_id}
🔄 *Status:* ${order.status.toUpperCase()}
📅 *Created:* ${new Date(order.created_at).toLocaleDateString()}
🚗 *VIN:* ${vin}
📦 *Total Parts:* ${order.parts_in_order?.total_items || 0}

*Parts List:*
${partsText}
    `);

    window.open(`https://wa.me/?text=${message}`, '_blank');
  }
}

// Export singleton instance
export const orderExportService = new OrderExportService();
